import { useState } from 'react';
import { Button, Stack, TextField, Typography } from '@mui/material';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import { useSeedMatchScore, type SeedScoreDto } from '../../api/useMatchEvents';

const digits = (v: string) => v.replace(/[^0-9]/g, '');

/**
 * Operator "set score" control for establishing (or correcting) the backend
 * `MatchState` baseline when the LSports feed cold-started mid-match and no
 * authoritative score exists yet (the `Incidents` feed has no replay/backfill on
 * connect, so a pre-connect goal is never re-sent). It POSTs to
 * `POST /match-events/matches/:fixtureId/seed-score`; the engine interprets the
 * seed and re-publishes the authoritative `MatchState` over the `match_state` WS
 * event — the FE renders that backend state, NEVER the typed values directly.
 *
 * `mode: 'seed'` (no baseline yet) opens the inputs by default. `mode:
 * 'override'` (a baseline exists — the feed can mis-state a score) starts
 * collapsed behind a "Correct score" button. Corners are optional; scores are
 * required. Failures surface through the global mutation-error toast (the
 * mutation carries a `mutationKey`) — never a silent failure.
 */
export function SeedScoreControl({
  fixtureId,
  homeTeam,
  awayTeam,
  mode,
}: {
  fixtureId: number;
  homeTeam: string;
  awayTeam: string;
  mode: 'seed' | 'override';
}) {
  const seed = useSeedMatchScore();
  const [open, setOpen] = useState(mode === 'seed');
  const [home, setHome] = useState('');
  const [away, setAway] = useState('');
  const [homeCorners, setHomeCorners] = useState('');
  const [awayCorners, setAwayCorners] = useState('');

  const canSubmit = home !== '' && away !== '' && !seed.isLoading;

  const reset = () => {
    setHome('');
    setAway('');
    setHomeCorners('');
    setAwayCorners('');
  };

  const submit = () => {
    if (!canSubmit) return;
    const dto: SeedScoreDto = { homeScore: Number(home), awayScore: Number(away) };
    if (homeCorners !== '') dto.homeCorners = Number(homeCorners);
    if (awayCorners !== '') dto.awayCorners = Number(awayCorners);
    seed.mutate(
      { fixtureId, dto },
      {
        onSuccess: () => {
          reset();
          // A corrected baseline collapses back; a fresh seed leaves the inputs
          // open until the backend state arrives and swaps this out for the board.
          if (mode === 'override') setOpen(false);
        },
      },
    );
  };

  if (!open) {
    return (
      <Button
        size="small"
        variant="text"
        startIcon={<SportsScoreIcon fontSize="small" />}
        onClick={() => setOpen(true)}
      >
        Correct score
      </Button>
    );
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Set score
      </Typography>
      <TextField
        size="small"
        label={homeTeam || 'Home'}
        value={home}
        onChange={(e) => setHome(digits(e.target.value))}
        inputProps={{ inputMode: 'numeric', 'aria-label': 'home score' }}
        sx={{ width: 96 }}
      />
      <Typography variant="body2" color="text.secondary">
        –
      </Typography>
      <TextField
        size="small"
        label={awayTeam || 'Away'}
        value={away}
        onChange={(e) => setAway(digits(e.target.value))}
        inputProps={{ inputMode: 'numeric', 'aria-label': 'away score' }}
        sx={{ width: 96 }}
      />
      <TextField
        size="small"
        label="🚩 H"
        value={homeCorners}
        onChange={(e) => setHomeCorners(digits(e.target.value))}
        inputProps={{ inputMode: 'numeric', 'aria-label': 'home corners' }}
        sx={{ width: 74 }}
      />
      <TextField
        size="small"
        label="🚩 A"
        value={awayCorners}
        onChange={(e) => setAwayCorners(digits(e.target.value))}
        inputProps={{ inputMode: 'numeric', 'aria-label': 'away corners' }}
        sx={{ width: 74 }}
      />
      <Button size="small" variant="contained" onClick={submit} disabled={!canSubmit}>
        {seed.isLoading ? 'Setting…' : 'Set'}
      </Button>
      {mode === 'override' && (
        <Button size="small" onClick={() => setOpen(false)} disabled={seed.isLoading}>
          Cancel
        </Button>
      )}
    </Stack>
  );
}
